import { useState, useEffect } from 'react'
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useCare } from '../../contexts/CareContext'
import QuickAddSheet from './QuickAddSheet'

function Avatar({ url, name, size = 38 }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase()
  return url ? (
    <img
      src={url}
      alt={name}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
    />
  ) : (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--pa)',
        color: '#fff',
        fontWeight: 700,
        fontSize: size * 0.42,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  )
}

const STAGE_LABEL = {
  early: 'Early stage',
  middle: 'Middle stage',
  late: 'Late stage',
  unknown: null,
}

const CAREGIVER_NAV = [
  { to: '/', label: 'Home', icon: 'ti-home', end: true },
  { to: '/?add=care-log', label: 'Log', icon: 'ti-notes' },
  { to: '/more', label: 'More', icon: 'ti-dots' },
]

const NAV = [
  { to: '/', label: 'Home', icon: 'ti-home', end: true },
  { to: '/goals', label: 'Goals', icon: 'ti-target' },
  { to: '/tasks', label: 'Tasks', icon: 'ti-checklist' },
  { to: '/tracking', label: 'Tracking', icon: 'ti-chart-line' },
  { to: '/ginie', label: 'Ginie', icon: 'ti-sparkles' },
  { to: '/more', label: 'More', icon: 'ti-dots' },
]

export default function AppShell() {
  const { recipient, memberships, setActiveId, isCaregiver } = useCare()
  const navigate = useNavigate()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetInitial, setSheetInitial] = useState(null)
  const location = useLocation()

  // ?add=care-log|vitals|incident|appointment opens the sheet on that form.
  useEffect(() => {
    const add = new URLSearchParams(location.search).get('add')
    if (!add) return
    setSheetInitial(['care-log', 'vitals', 'incident', 'appointment'].includes(add) ? add : null)
    setSheetOpen(true)
    navigate(location.pathname, { replace: true })
  }, [location.search, location.pathname, navigate])

  const stage = STAGE_LABEL[recipient?.care_stage] || null

  return (
    <div className="screen">
      {/* Header — avatar, name, care stage */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="container spread" style={{ height: 62 }}>
          <button
            type="button"
            className="row"
            onClick={() => navigate('/profile')}
            style={{ background: 'none', border: 'none', padding: 0, minWidth: 0 }}
          >
            <Avatar url={recipient?.photo_url} name={recipient?.full_name} />
            <div style={{ textAlign: 'left', lineHeight: 1.25, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {recipient?.preferred_name || recipient?.full_name || 'Care recipient'}
              </div>
              {stage && (
                <span
                  className="badge"
                  style={{ fontSize: 11, padding: '1px 8px', marginTop: 2 }}
                >
                  {stage}
                </span>
              )}
            </div>
          </button>

          {memberships.length > 1 && (
            <select
              className="select"
              value={recipient?.id || ''}
              onChange={(e) => setActiveId(e.target.value)}
              style={{ width: 'auto', minHeight: 40, maxWidth: 130 }}
              aria-label="Switch care recipient"
            >
              {memberships.map((m) => (
                <option key={m.recipient.id} value={m.recipient.id}>
                  {m.recipient.preferred_name || m.recipient.full_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>

      {/* Routed page */}
      <main className="container page" style={{ paddingTop: 16 }}>
        <Outlet />
      </main>

      {/* Floating action button */}
      <button
        className="fab"
        aria-label="Quick add"
        aria-expanded={sheetOpen}
        onClick={() => { setSheetInitial(null); setSheetOpen((v) => !v) }}
      >
        <i className="ti ti-plus" aria-hidden />
      </button>

      {sheetOpen && (
        <QuickAddSheet
          initial={sheetInitial}
          onClose={() => { setSheetOpen(false); setSheetInitial(null) }}
        />
      )}

      {/* Bottom nav — 6 tabs */}
      <nav className="tabbar">
        <div className="tabbar-inner">
          {(isCaregiver ? CAREGIVER_NAV : NAV).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `tab${isActive ? ' is-active' : ''}`}
            >
              <i className={`ti ${item.icon}`} aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
